/**
 * Schedule Store
 * ──────────────
 * Manages daily schedule state: completions, check-offs, swaps.
 * localStorage persistence + Firestore sync when authenticated.
 *
 * Pattern: optimistic local update → background Firestore write.
 * If Firestore fails, localStorage still has the data.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { syncScheduleDay } from './firestore-sync';
import { localDateStr } from './date-utils';

// Injected by plan-store to avoid circular import (plan-store → schedule-store)
let _getPlanDay: ((date: string) => { isRest: boolean; isOptional: boolean } | null) = () => null;
export function _injectGetPlanDay(fn: (date: string) => any) { _getPlanDay = fn; }

export interface DayState {
  date: string;
  morningRoutineDone: boolean;
  session2Done: boolean;
  sessionCompleted: boolean;
  sessionPartial: boolean;          // synced run below 80% of planned km (REQ-SYNC-05)
  completedBy: 'auto' | 'manual' | null;  // 'auto' = webhook match; manual always wins
  sessionSkipped: boolean;
  crossTrainingSwap: string | null;
  swapReason: string | null;        // Why the athlete swapped
  completedData: {
    actualKm?: number;
    avgPace?: string;
    avgHr?: number;
    maxHr?: number;
    duration?: number;
  } | null;
  checkedOffTime: number | null;
  sessionFeel: number | null;       // 1-5 scale
  sessionNote: string | null;       // optional post-run note
  postRunReview: string | null;     // AI-generated feedback
}

interface ScheduleState {
  // State
  days: Record<string, DayState>;       // keyed by "YYYY-MM-DD"
  _planVersion: number;                 // bumped by plan-store to trigger re-renders

  // Actions
  getDayState: (date: string) => DayState;
  toggleMorningRoutine: (date: string) => void;
  toggleSession2: (date: string) => void;
  markSessionCompleted: (date: string, data?: DayState['completedData']) => void;
  unmarkSession: (date: string) => void;
  setPostRunFeedback: (date: string, feel: number, note?: string | null, review?: string | null) => void;
  markSessionSkipped: (date: string) => void;
  swapToCrossTraining: (date: string, altName: string, reason?: string) => void;
  undoSwap: (date: string) => void;

  // Derived
  getRecentCompletionHistory: (days: number) => { date: string; completed: boolean; session2Done: boolean; morningRoutineDone: boolean }[];
  getSkipStreak: () => number;
  getCompliancePercent: (days: number) => number;
}

const emptyDay = (date: string): DayState => ({
  date,
  morningRoutineDone: false,
  session2Done: false,
  sessionCompleted: false,
  sessionPartial: false,
  completedBy: null,
  sessionSkipped: false,
  crossTrainingSwap: null,
  swapReason: null,
  completedData: null,
  checkedOffTime: null,
  sessionFeel: null,
  sessionNote: null,
  postRunReview: null,
});

export const useScheduleStore = create<ScheduleState>()(
  persist(
    (set, get) => ({
      days: {},
      _planVersion: 0,

      getDayState: (date) => {
        return get().days[date] ?? emptyDay(date);
      },

      // Undo completion — only used by UI buttons, never by coach
      unmarkSession: (date) => {
        set((state) => {
          const current = state.days[date] ?? emptyDay(date);
          const updated = {
            ...current,
            sessionCompleted: false,
            sessionPartial: false,
            completedBy: null,
            completedData: null,
            checkedOffTime: null,
          };
          syncScheduleDay(date, updated);
          return { days: { ...state.days, [date]: updated } };
        });
      },

      setPostRunFeedback: (date, feel, note, review) => {
        set((state) => {
          const current = state.days[date] ?? emptyDay(date);
          const updated = { ...current, sessionFeel: feel, sessionNote: note ?? null, postRunReview: review ?? null };
          syncScheduleDay(date, updated);
          return { days: { ...state.days, [date]: updated } };
        });
      },

      toggleMorningRoutine: (date) => {
        set((state) => {
          const current = state.days[date] ?? emptyDay(date);
          const updated = { ...current, morningRoutineDone: !current.morningRoutineDone };
          syncScheduleDay(date, updated);
          return { days: { ...state.days, [date]: updated } };
        });
      },

      toggleSession2: (date) => {
        set((state) => {
          const current = state.days[date] ?? emptyDay(date);
          const updated = { ...current, session2Done: !current.session2Done };
          syncScheduleDay(date, updated);
          return { days: { ...state.days, [date]: updated } };
        });
      },

      markSessionCompleted: (date, data) => {
        set((state) => {
          const current = state.days[date] ?? emptyDay(date);
          // SET to completed — never toggle back. Use markSessionSkipped to undo.
          const updated = {
            ...current,
            sessionCompleted: true,
            sessionPartial: false,
            completedBy: 'manual' as const,
            sessionSkipped: false,
            completedData: data ?? current.completedData ?? null,
            checkedOffTime: current.checkedOffTime ?? Date.now(),
          };
          syncScheduleDay(date, updated);
          return { days: { ...state.days, [date]: updated } };
        });
      },

      markSessionSkipped: (date) => {
        set((state) => {
          const current = state.days[date] ?? emptyDay(date);
          const updated = { ...current, sessionSkipped: true, sessionCompleted: false };
          syncScheduleDay(date, updated);
          return { days: { ...state.days, [date]: updated } };
        });
      },

      swapToCrossTraining: (date, altName, reason) => {
        set((state) => {
          const current = state.days[date] ?? emptyDay(date);
          const updated = { ...current, crossTrainingSwap: altName, swapReason: reason ?? null };
          syncScheduleDay(date, updated);
          return { days: { ...state.days, [date]: updated } };
        });
      },

      undoSwap: (date) => {
        set((state) => {
          const current = state.days[date] ?? emptyDay(date);
          const updated = { ...current, crossTrainingSwap: null };
          syncScheduleDay(date, updated);
          return { days: { ...state.days, [date]: updated } };
        });
      },

      getRecentCompletionHistory: (numDays) => {
        const { days } = get();
        const result = [];
        const today = new Date();
        for (let i = 0; i < numDays; i++) {
          const d = new Date(today);
          d.setDate(d.getDate() - i);
          const dateStr = localDateStr(d);
          const state = days[dateStr];
          result.push({
            date: dateStr,
            completed: state?.sessionCompleted ?? false,
            session2Done: state?.session2Done ?? false,
            morningRoutineDone: state?.morningRoutineDone ?? false,
          });
        }
        return result;
      },

      getSkipStreak: () => {
        const { days } = get();
        let streak = 0;
        const today = new Date();
        for (let i = 1; i <= 14; i++) {
          const d = new Date(today);
          d.setDate(d.getDate() - i);
          const dateStr = localDateStr(d);
          const state = days[dateStr];
          if (state?.sessionSkipped) {
            streak++;
          } else if (state?.sessionCompleted) {
            break;
          }
        }
        return streak;
      },

      getCompliancePercent: (numDays) => {
        const history = get().getRecentCompletionHistory(numDays);
        if (history.length === 0) return 100;
        // Exclude rest days and optional sessions from the denominator
        let required = 0;
        let completed = 0;
        for (const h of history) {
          const plan = _getPlanDay(h.date);
          if (plan?.isRest || plan?.isOptional) continue; // skip rest & optional
          required++;
          if (h.completed) completed++;
        }
        if (required === 0) return 100;
        return Math.round((completed / required) * 100);
      },
    }),
    {
      name: 'kader-schedule',  // localStorage key
    }
  )
);

/**
 * Hydrate schedule state from Firestore.
 * Replaces local state entirely with the authenticated user's Firestore data.
 * Called after authentication — non-blocking.
 *
 * Uses replace (not merge) so a previous user's local state never bleeds through.
 */
export async function hydrateFromFirestore(): Promise<void> {
  try {
    const { getDocs, collection, query, orderBy, limit: fsLimit } = await import('firebase/firestore');
    const { db: firestoreDb } = await import('./firebase');
    const { useAuthStore } = await import('./auth-store');

    const uid = useAuthStore.getState().user?.uid;
    if (!uid) return;

    // Read last 90 days of schedule state from Firestore
    const q = query(
      collection(firestoreDb, 'athletes', uid, 'schedule'),
      orderBy('updatedAt', 'desc'),
      fsLimit(90),
    );
    const snap = await getDocs(q);

    // Always replace — even if empty, this clears any stale previous-user data
    const newDays: Record<string, DayState> = {};
    snap.docs.forEach(doc => {
      const data = doc.data() as DayState & { updatedAt?: number };
      newDays[doc.id] = { ...emptyDay(doc.id), ...data };
    });

    useScheduleStore.setState({ days: newDays });
    console.log(`[schedule-store] Replaced with ${snap.docs.length} days from Firestore (uid: ${uid})`);

    // Listen for server-side writes (webhook auto-completion) landing while
    // the app is open — merges the changed day into local state.
    attachScheduleListener();
  } catch (err) {
    console.warn('[schedule-store] Firestore hydration failed:', err);
  }
}

let scheduleUnsubscribe: (() => void) | null = null;

function attachScheduleListener(): void {
  import('./firestore-sync').then(({ subscribeScheduleRange }) => {
    import('./date-utils').then(({ localDateOffset }) => {
      scheduleUnsubscribe?.();
      scheduleUnsubscribe = subscribeScheduleRange(
        localDateOffset(-7),
        localDateOffset(1),
        (date, data) => {
          useScheduleStore.setState((state) => ({
            days: { ...state.days, [date]: { ...emptyDay(date), ...state.days[date], ...(data as Partial<DayState>) } },
          }));
        },
      );
    }).catch(() => {});
  }).catch(() => {});
}
